import {
    CloseButton,
    CrudMode,
    Stack,
    Switch,
    Typography,
    UpdateButton,
    newToast,
    ValidationErrors,
    DropDownMenu,
    MenuItemType,
    ListContextValue,
} from "@compass/core";
import { SubmitButton } from "components";
import { ScholarshipStatus, Permission, PermissionAction } from "generated/openApi";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import { ListContext } from "@compass/core";
import ApiService from "services/apiService";

export const ScholarshipStatusPageHeader = (props: {
    scholarshipStatus: ScholarshipStatus;
    rootPermission: Permission;
    handle: string;
    crudMode: CrudMode;
    fetch: (id: number) => void;
    reroute: (id: number, crudMode: CrudMode) => void;
    setScholarshipStatus: (scholarshipStatus: ScholarshipStatus) => void;
    initScholarshipStatus: ScholarshipStatus;
    setValidationErrors: (errors: ValidationErrors) => void;
    unsavedChanges: boolean;
    resetUnsavedChanges: (items: any[]) => void;
    closeOnSave?: boolean | undefined;
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const id = props.scholarshipStatus.id;
    const [createAnother, setCreateAnother] = useState<boolean>(false);
    const listContext = useContext<ListContextValue>(ListContext);
    const { loadList } = useContext(ListContext);

    const save = (scholarshipStatus: ScholarshipStatus) => {
        props.setValidationErrors({});

        if (id > 0) {
            ApiService.scholarshipStatus
                .saveUpdate(scholarshipStatus)
                .then(() => {
                    props.fetch(id);
                    props.reroute(id, "view");
                    listContext.loadList?.();
                    dispatch(setAppToast(newToast("success")));
                })
                .catch((ex) => {
                    dispatch(setAppToast(newToast("error")));
                    props.setValidationErrors(ex.response);
                });
        } else {
            ApiService.scholarshipStatus
                .saveCreate(scholarshipStatus)
                .then((response) => {
                    const newId = response;
                    if (createAnother) {
                        props.setScholarshipStatus(props.initScholarshipStatus);
                        props.resetUnsavedChanges([props.initScholarshipStatus]);
                    } else {
                        props.fetch(newId);
                        props.reroute(newId, "view");
                    }
                    listContext.loadList?.();
                    dispatch(setAppToast(newToast("success")));
                })
                .catch((ex) => {
                    dispatch(setAppToast(newToast("error")));
                    props.setValidationErrors(ex.response);
                });
        }
    };

    const postDelete = (id: number) => {
        if (window.confirm("Are you sure you want to delete this Scholarship Status?")) {
            ApiService.scholarshipStatus.delete(id).then(() => {
                navigate("../");
                loadList?.({ resetPage: true });
                dispatch(setAppToast(newToast("success")));
            });
        }
    };

    const menuItems: MenuItemType[] = [];
    if (id > 0) {
        if (isAuthorized(props.rootPermission, PermissionAction.Delete))
            menuItems.push({
                label: <Typography color="error">Delete</Typography>,
                action: () => postDelete(id),
            });
    }

    return (
        <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing="sm">
                <Typography variant="inherit">
                    {id === 0 ? "New" : ""} {props.handle}
                </Typography>
                {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
            </Stack>

            <Stack direction="row" alignItems="center" spacing="sm">
                {id === 0 && (
                    <Switch
                        value={createAnother}
                        onChange={(e) => setCreateAnother(e.target.checked)}
                        label="+ more"
                    />
                )}

                <CloseButton theme="Dark" confirm={props.unsavedChanges} />

                {props.crudMode === "view" &&
                    isAuthorized(props.rootPermission, PermissionAction.Update) && (
                        <UpdateButton theme="Dark" onClick={() => props.reroute(id, "edit")} />
                    )}

                {props.crudMode === "edit" && (
                    <SubmitButton
                        theme="Dark"
                        onClick={() => save(props.scholarshipStatus)}
                        disabled={!props.unsavedChanges}
                    />
                )}
            </Stack>
        </Stack>
    );
};
